import { call, put, select, takeLatest } from "redux-saga/effects";
import { axiosRestInstance } from "../../../common/axios";
import { AxiosResponse } from "axios";
import { externalContractSliceAction } from "../../slices/external-contract";
import {
  ExternalContractType,
  WorkPackagesType,
} from "../../slices/external-contract/Types";
import {
  addExternalContractPayloadType,
  externalContractSagaActionTypes,
  getExternalContractSagaAction,
} from "./saga-actions";
import { RootStateType } from "../..";
import { myAccountSliceAction } from "../../slices/my-account";

type getExternalContractAPIResponseType = {
  success: boolean;
  message: string;
  data: ExternalContractType[];
};

const getExternalContractAPI = async (ID: number) => {
  let response = axiosRestInstance
    .get<AxiosResponse<getExternalContractAPIResponseType>>(
      "/externalServiceContract/getAllExternalServiceContracts/" + ID
    )
    .then((res) => res.data);
  return response;
};

type addExternalContractAPIResponseType = {
  success: boolean;
  message: string;
  data: any;
};

const addExternalContractAPI = async (data: addExternalContractPayloadType) => {
  let response = axiosRestInstance
    .post<AxiosResponse<addExternalContractAPIResponseType>>(
      "/externalServiceContract/insertExternalServiceContract/" + data.ID,
      data.externalContract
    )
    .then((res) => res.data);
  return response;
};

type updateExternalContractAPIResponseType = {
  success: boolean;
  message: string;
  data: any;
};

const updateExternalContractAPI = async (
  externalContract: ExternalContractType
) => {
  let response = axiosRestInstance
    .put<AxiosResponse<updateExternalContractAPIResponseType>>(
      "/externalServiceContract/updateExternalServiceContract/" +
        externalContract.ID,
      externalContract
    )
    .then((res) => res.data);
  return response;
};

type deleteExternalContractAPIResponseType = {
  success: boolean;
  message: string;
  data: any;
};

const deleteExternalContractAPI = async (externalContractID: number) => {
  let response = axiosRestInstance
    .delete<AxiosResponse<deleteExternalContractAPIResponseType>>(
      "/externalServiceContract/deleteExternalServiceContract/" +
        externalContractID
    )
    .then((res) => res.data);
  return response;
};

type getExternalContractDetailAPIResponseType = {
  success: boolean;
  message: string;
  data: ExternalContractType;
};

const getExternalContractDetailAPI = async (ID: number) => {
  let response = axiosRestInstance
    .get<AxiosResponse<getExternalContractDetailAPIResponseType>>(
      "/externalServiceContract/getExternalServiceContract/" + ID
    )
    .then((res) => res.data);
  return response;
};

type getWorkPackagesResponseType = {
  success: boolean;
  message: string;
  data: WorkPackagesType[];
};

const getWorkPackagesAPI = async () => {
  let response = axiosRestInstance
    .get<AxiosResponse<getWorkPackagesResponseType>>(
      "/lookupData/getlookupData/workPackage"
    )
    .then((res) => res.data);
  return response;
};

type getTypeOfEngagementsResponseType = {
  success: boolean;
  message: string;
  data: {
    value: string;
  }[];
};

const getTypeOfEngagementsAPI = async () => {
  let response = axiosRestInstance
    .get<AxiosResponse<getTypeOfEngagementsResponseType>>(
      "/lookupData/getlookupData/engagement"
    )
    .then((res) => res.data);
  return response;
};

// API ABOVE --- SAGA BELOW

function* getExternalContractSaga(action: {
  type: string;
  payload: {
    ID: number;
  };
}) {
  try {
    yield put(externalContractSliceAction.setExternalContractLoading(true));
    let response: getExternalContractAPIResponseType = yield call(
      getExternalContractAPI,
      action.payload.ID
    );
    const list = response.data ? response.data.reverse() : [];
    yield put(externalContractSliceAction.setExternalContracts(list));
    yield put(externalContractSliceAction.setExternalContractLoading(false));
    yield put(
      externalContractSliceAction.setFetchedExternalContractsPartnerID(
        action.payload.ID
      )
    );
  } catch (e) {
    yield put(externalContractSliceAction.setExternalContracts([]));
    yield put(externalContractSliceAction.setExternalContractLoading(false));
    console.log(e);
  }
}

function* addExternalContractSaga(action: {
  type: string;
  payload: addExternalContractPayloadType;
}) {
  try {
    yield put(externalContractSliceAction.setAddExternalContractLoading(true));
    let response: addExternalContractAPIResponseType = yield call(
      addExternalContractAPI,
      action.payload
    );
    yield put(externalContractSliceAction.setAddExternalContractLoading(false));
    yield put(externalContractSliceAction.setAddExternalContractStatus(true));
    yield put(getExternalContractSagaAction(action.payload.ID));
  } catch (e) {
    yield put(externalContractSliceAction.setAddExternalContractLoading(false));
    yield put(externalContractSliceAction.setAddExternalContractStatus(false));
    console.log(e);
  }
}

function* updateExternalContractSaga(action: {
  type: string;
  payload: {
    externalContract: ExternalContractType;
  };
}) {
  try {
    yield put(
      externalContractSliceAction.setUpdateExternalContractLoading(true)
    );
    let response: updateExternalContractAPIResponseType = yield call(
      updateExternalContractAPI,
      action.payload.externalContract
    );
    yield put(
      externalContractSliceAction.setUpdateExternalContractLoading(false)
    );
    yield put(
      externalContractSliceAction.setUpdateExternalContractStatus(true)
    );
    yield put(
      getExternalContractSagaAction(action.payload.externalContract.partner_id)
    );
  } catch (e) {
    yield put(
      externalContractSliceAction.setUpdateExternalContractLoading(false)
    );
    yield put(
      externalContractSliceAction.setUpdateExternalContractStatus(false)
    );
    console.log(e);
  }
}

function* deleteExternalContractSaga(action: {
  type: string;
  payload: {
    data: {
      externalContractID: number;
      setModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
    };
  };
}) {
  try {
    yield put(
      externalContractSliceAction.setDeleteExternalContractLoading(true)
    );
    let response: deleteExternalContractAPIResponseType = yield call(
      deleteExternalContractAPI,
      action.payload.data.externalContractID
    );

    // Remove the external contract from the external contract slice
    const selectExternalContracts = (state: RootStateType) =>
      state.external_contract;
    const { externalContracts } = yield select(selectExternalContracts);
    const updatedExternalContracts = externalContracts.filter(
      (externalContract: any) => {
        return externalContract.ID !== action.payload.data.externalContractID;
      }
    );
    yield put(
      externalContractSliceAction.setExternalContracts(updatedExternalContracts)
    );
    // Remove the external contract from the my account slice
    const selectContracts = (state: RootStateType) => state.my_account;
    const { contracts } = yield select(selectContracts);
    const updatedContracts = contracts.filter((contract: any) => {
      return contract.ID !== action.payload.data.externalContractID;
    });
    yield put(myAccountSliceAction.setContracts(updatedContracts));
    
    yield put(
      externalContractSliceAction.setDeleteExternalContractStatus(true)
    );
    action.payload.data.setModalOpen(false);
  } catch (e) {
    yield put(
      externalContractSliceAction.setDeleteExternalContractStatus(false)
    );
    console.log(e);
  } finally {
    yield put(
      externalContractSliceAction.setDeleteExternalContractLoading(false)
    );
  }
}

function* getExternalContractDetailSaga(action: {
  type: string;
  payload: {
    ID: number;
  };
}) {
  try {
    let response: getExternalContractDetailAPIResponseType = yield call(
      getExternalContractDetailAPI,
      action.payload.ID
    );
    yield put(
      externalContractSliceAction.setExternalContractDetails(response.data)
    );
  } catch (e) {
    console.log(e);
  }
}

function* getWorkPackagesSaga(action: { type: string }) {
  try {
    let response: getWorkPackagesResponseType = yield call(getWorkPackagesAPI);
    yield put(externalContractSliceAction.setWorkPackages(response.data));
  } catch (e) {
    console.log(e);
  }
}

function* getTypeOfEngagementsSaga(action: { type: string }) {
  try {
    yield put(externalContractSliceAction.setTypeOfEngagementsLoading(true));
    let response: getTypeOfEngagementsResponseType = yield call(
      getTypeOfEngagementsAPI
    );
    yield put(externalContractSliceAction.setTypeOfEngagements(response.data));
  } catch (e) {
    console.log(e);
  } finally {
    yield put(externalContractSliceAction.setTypeOfEngagementsLoading(false));
  }
}

function* externalContractSaga() {
  yield takeLatest(
    externalContractSagaActionTypes.callGetExternalContractSaga,
    getExternalContractSaga
  );
  yield takeLatest(
    externalContractSagaActionTypes.callAddExternalContractSaga,
    addExternalContractSaga
  );
  yield takeLatest(
    externalContractSagaActionTypes.callUpdateExternalContractSaga,
    updateExternalContractSaga
  );
  yield takeLatest(
    externalContractSagaActionTypes.callDeleteExternalContractSaga,
    deleteExternalContractSaga
  );
  yield takeLatest(
    externalContractSagaActionTypes.callGetExternalContractDetailSaga,
    getExternalContractDetailSaga
  );
  yield takeLatest(
    externalContractSagaActionTypes.callGetWorkPackagesSaga,
    getWorkPackagesSaga
  );
  yield takeLatest(
    externalContractSagaActionTypes.callGetTypeOfEngagementsSaga,
    getTypeOfEngagementsSaga
  );
}

export default externalContractSaga;
